import type { AppLanguage } from './i18n/languageStore';
import { readScopedStorage, writeScopedStorage } from './userPreferenceScope';

export type FontSizePreset = 'compact' | 'comfortable' | 'large' | 'extraLarge' | 'maximum';

export type FontPreference =
  | { mode: 'preset'; preset: FontSizePreset }
  | { mode: 'custom'; scale: number };

export const FONT_STORAGE_KEY = 'ef_app_font_scale_v1';
export const FONT_CHANGED_EVENT = 'ef-font-changed';

export const FONT_SCALE_MIN = 0.88;
export const FONT_SCALE_MAX = 1.32;
export const FONT_SCALE_STEP = 0.01;

export const FONT_PRESETS: {
  id: FontSizePreset;
  labelKey: string;
  scale: number;
  mobileScale: number;
}[] = [
  { id: 'compact', labelKey: 'font.compact', scale: 0.94, mobileScale: 1 },
  { id: 'comfortable', labelKey: 'font.comfortable', scale: 1, mobileScale: 1.06 },
  { id: 'large', labelKey: 'font.large', scale: 1.08, mobileScale: 1.14 },
  { id: 'extraLarge', labelKey: 'font.extraLarge', scale: 1.16, mobileScale: 1.22 },
  { id: 'maximum', labelKey: 'font.maximum', scale: 1.24, mobileScale: 1.28 },
];

function languageMobileBoost(language: AppLanguage): number {
  if (language === 'hi' || language === 'te') return 1.05;
  return 1;
}

function isPreset(value: string): value is FontSizePreset {
  return FONT_PRESETS.some((p) => p.id === value);
}

function parseFontPreference(raw: string | null): FontPreference {
  if (!raw) return { mode: 'preset', preset: 'comfortable' };
  try {
    const parsed = JSON.parse(raw) as FontPreference;
    if (parsed.mode === 'custom' && typeof parsed.scale === 'number') {
      return { mode: 'custom', scale: clampScale(parsed.scale) };
    }
    if (parsed.mode === 'preset' && isPreset(parsed.preset)) {
      return parsed;
    }
  } catch {
    /* legacy string preset */
  }
  if (isPreset(raw)) return { mode: 'preset', preset: raw };
  return { mode: 'preset', preset: 'comfortable' };
}

export function readFontPreference(): FontPreference {
  return parseFontPreference(readScopedStorage(FONT_STORAGE_KEY));
}

export function writeFontPreference(pref: FontPreference): void {
  if (typeof localStorage === 'undefined') return;
  writeScopedStorage(FONT_STORAGE_KEY, JSON.stringify(pref));
  window.dispatchEvent(new Event(FONT_CHANGED_EVENT));
}

export function clampScale(scale: number): number {
  return Math.round(Math.min(FONT_SCALE_MAX, Math.max(FONT_SCALE_MIN, scale)) * 100) / 100;
}

export function presetMetrics(preset: FontSizePreset) {
  return FONT_PRESETS.find((p) => p.id === preset) ?? FONT_PRESETS[1];
}

export function resolveFontScales(pref: FontPreference): { scale: number; mobileScale: number } {
  if (pref.mode === 'custom') {
    const scale = clampScale(pref.scale);
    const mobileScale = clampScale(scale * 1.06);
    return { scale, mobileScale };
  }
  const metrics = presetMetrics(pref.preset);
  return { scale: metrics.scale, mobileScale: metrics.mobileScale };
}

export function readFontSizePreset(): FontSizePreset {
  const pref = readFontPreference();
  if (pref.mode === 'preset') return pref.preset;
  return 'comfortable';
}

export function writeFontSizePreset(preset: FontSizePreset): void {
  writeFontPreference({ mode: 'preset', preset });
}

export function applyFontPreferences(pref: FontPreference, language: AppLanguage = 'en'): void {
  if (typeof document === 'undefined') return;
  const { scale, mobileScale } = resolveFontScales(pref);
  const langBoost = languageMobileBoost(language);
  const mobile = mobileScale * langBoost;
  const root = document.documentElement;
  root.style.setProperty('--app-font-scale', String(scale));
  root.style.setProperty('--app-font-scale-mobile', String(mobile));
  root.dataset.fontScale = pref.mode === 'preset' ? pref.preset : 'custom';
  root.style.setProperty('--app-font-scale-percent', `${Math.round(scale * 100)}%`);
}

export function formatScalePercent(scale: number): string {
  return `${Math.round(scale * 100)}%`;
}

export function nearestPreset(scale: number): FontSizePreset {
  let closest = FONT_PRESETS[0];
  let minDiff = Math.abs(scale - closest.scale);
  for (const preset of FONT_PRESETS) {
    const diff = Math.abs(scale - preset.scale);
    if (diff < minDiff) {
      minDiff = diff;
      closest = preset;
    }
  }
  return closest.id;
}
